import { type FormEvent, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { i18n } from '@wms/shared';
import { ApiError, api } from '../lib/api.js';

const { t } = i18n;

export function PasswordResetConfirmPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 12) {
      setError(t('auth.password_reset.too_short'));
      return;
    }
    setBusy(true);
    try {
      await api('/auth/password-reset/confirm', {
        method: 'POST',
        body: { token, newPassword: password },
      });
      setDone(true);
      setTimeout(() => navigate('/login'), 1500);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'invalid_token') {
        setError(t('auth.password_reset.invalid_token'));
      } else {
        setError(t('common.error'));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <form onSubmit={(e) => void onSubmit(e)} className="w-full max-w-sm space-y-4 rounded-lg border border-border p-6">
        <h1 className="text-lg font-semibold">{t('auth.password_reset.confirm_title')}</h1>
        {!token ? (
          <p className="text-sm text-red-600">{t('auth.password_reset.no_token')}</p>
        ) : done ? (
          <p className="text-sm text-green-700 dark:text-green-300">
            {t('auth.password_reset.confirmed')}
          </p>
        ) : (
          <>
            <label className="block space-y-1">
              <span className="text-sm">{t('auth.password_reset.new_password')}</span>
              <input
                type="password"
                autoComplete="new-password"
                required
                minLength={12}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded border border-border bg-background px-2 py-1.5 text-sm"
              />
              <span className="block text-xs text-muted-foreground">
                {t('auth.password_reset.length_hint')}
              </span>
            </label>
            {error ? <div className="text-sm text-red-600">{error}</div> : null}
            <button
              type="submit"
              disabled={busy || password.length < 12 || !token}
              className="w-full rounded bg-primary text-primary-foreground py-1.5 text-sm font-medium disabled:opacity-50"
            >
              {busy ? t('common.loading') : t('auth.password_reset.confirm_submit')}
            </button>
          </>
        )}
        <Link to="/login" className="block text-xs text-muted-foreground hover:underline">
          ← {t('auth.login.title')}
        </Link>
      </form>
    </div>
  );
}
